/* istanbul ignore file */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import {
  Box,
  FileUpload,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import './styles.css';
import { extractSrcValues } from 'src/utils/mediaUtils';
import { sumBy } from 'lodash';
import FileDisplay, { FileMetadata } from '../FileDisplay';
import SafeHtml from '../SafeHtml';
import { MediaData } from 'src/types/helios/Media';

export interface FileData {
  image: string;
  extension: string;
  fileName?: string;
}
// Register the image resize module
Quill.register('modules/imageResize', ImageResize);

interface EditorProps {
  readonly placeholder?: string;
  readonly theme?: 'snow' | 'bubble' | string;
  onChange?: (content: string) => void;
  onChangeMedia?: (images: FileData[]) => void;
  onFilesChange?: (data: { files: File[]; metadata: FileMetadata[] }) => void;
  readonly value?: string;
  readonly minHeight?: number;
  readonly programId?: string;
  readonly businessUnit?: string;
  readonly createdBy?: string;
  readonly maxCharLength?: number;
  readonly readOnly?: boolean;
  readonly showFileUpload?: boolean;
  readonly initialFiles?: File[];
  readonly initialFileMetadata?: FileMetadata[];
  readonly mediaResponse?: MediaData[];
}

const TextEditor: React.FC<EditorProps> = ({
  placeholder,
  theme = 'snow',
  onChange,
  onChangeMedia,
  onFilesChange,
  value,
  minHeight,
  readOnly,
  maxCharLength = 2000,
  showFileUpload = false,
  initialFiles = [],
  initialFileMetadata = [],
  mediaResponse = [],
}) => {
  // Create a ref to store the Quill instance
  const quillRef = React.useRef<ReactQuill>(null);
  const [imageList, setImageList] = useState<FileData[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(initialFiles);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>(initialFileMetadata);

  // Use refs to track previous values and prevent infinite loops
  const prevInitialFilesRef = useRef<File[]>(initialFiles);
  const prevInitialFileMetadataRef = useRef<FileMetadata[]>(initialFileMetadata);

  // Sync state when props actually change (deep comparison by file names and metadata IDs)
  useEffect(() => {
    // Compare by length first (fast check)
    if (initialFiles.length !== prevInitialFilesRef.current.length) {
      setUploadedFiles(initialFiles);
      prevInitialFilesRef.current = initialFiles;
      return;
    }

    // If lengths match, check if we went from empty to non-empty or if file names changed
    const hasNewFiles = initialFiles.length > 0 && uploadedFiles.length === 0;
    const fileNamesChanged = initialFiles.some(
      (file, idx) => file.name !== prevInitialFilesRef.current[idx]?.name,
    );

    if (hasNewFiles || fileNamesChanged) {
      setUploadedFiles(initialFiles);
      prevInitialFilesRef.current = initialFiles;
    }
  }, [initialFiles, uploadedFiles.length]);

  useEffect(() => {
    // Compare by length first (fast check)
    if (initialFileMetadata.length !== prevInitialFileMetadataRef.current.length) {
      setFileMetadata(initialFileMetadata);
      prevInitialFileMetadataRef.current = initialFileMetadata;
      return;
    }

    // If lengths match, check if we went from empty to non-empty or if mediaIds changed
    const hasNewMetadata = initialFileMetadata.length > 0 && fileMetadata.length === 0;
    const mediaIdsChanged = initialFileMetadata.some(
      (meta, idx) => meta.mediaId !== prevInitialFileMetadataRef.current[idx]?.mediaId,
    );

    if (hasNewMetadata || mediaIdsChanged) {
      setFileMetadata(initialFileMetadata);
      prevInitialFileMetadataRef.current = initialFileMetadata;
    }
  }, [initialFileMetadata, fileMetadata.length]);

  // Handle image upload
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg, image/png');
    input.click();

    input.onchange = async () => {
      if (input.files && input.files[0]) {
        const file = input.files[0];
        const contentType = file.type;

        // Validate content type against allowed types
        if (!['image/jpeg', 'image/png'].includes(contentType)) {
          console.error('Invalid file type. Only JPEG, PNG, and PDF files are allowed.');
          return;
        }
        try {
          // Convert the file to a data URL for immediate display
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            const quill = quillRef.current?.getEditor();

            if (quill) {
              // Get the current cursor position
              const range = quill.getSelection(true);
              // Insert the image at the cursor position
              quill.insertEmbed(range.index, 'image', dataUrl);

              // Move the cursor after the image
              quill.setSelection(range.index + 1, 0);

              // Create FileData object from dataUrl
              const fileData: FileData = {
                image: dataUrl,
                extension: contentType,
              };

              // Add FileData to imageList only if it doesn't already exist
              setImageList((prev) => {
                if (!prev.some((item) => item.image === dataUrl)) {
                  return [...prev, fileData];
                }
                return prev;
              });
            }
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      }
    };
  };

  // Make the Quill instance available globally for the image handler
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      (window as any).quill = editor;

      // Add custom handler for image toolbar button
      const toolbar = editor.getModule('toolbar');
      toolbar.addHandler('image', handleImageUpload);
    }
  }, []);
  useEffect(() => {
    if (onChangeMedia) {
      onChangeMedia(imageList);
    }
  }, [imageList]);

  // Handle FileUpload component file changes
  const handleFileUploadChange = async (files: File[]) => {
    setUploadedFiles(files);

    // Notify parent component of file changes with both files and metadata
    if (onFilesChange) {
      onFilesChange({ files, metadata: fileMetadata });
    }

    // Convert files to FileData format
    const fileDataPromises = files.map(async (file) => {
      return new Promise<FileData>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          // Extract filename without extension
          const fileName =
            file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
          console.log(
            'TextEditor - Extracted fileName:',
            fileName,
            'from file:',
            file.name,
          );
          resolve({
            image: dataUrl,
            extension: file.type,
            fileName: fileName,
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    try {
      const newFileDataList = await Promise.all(fileDataPromises);
      setImageList(newFileDataList);
    } catch (error) {
      console.error('Error converting files to base64:', error);
    }
  };

  // Handle file removal in edit mode
  const handleFileRemove = (mediaId: string) => {
    const updatedMetadata = fileMetadata.filter((file) => file.mediaId !== mediaId);
    setFileMetadata(updatedMetadata);

    // Notify parent component with both updated files and metadata
    if (onFilesChange) {
      onFilesChange({ files: uploadedFiles, metadata: updatedMetadata });
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getTextLength = useCallback(() => {
    const length = value?.length || 0;
    const attachedImages = extractSrcValues(value || '');
    const totaImageTextLength = sumBy(attachedImages, function (o) {
      return o.length;
    });
    if (attachedImages.length > 0) {
      return length - totaImageTextLength + 36 * attachedImages.length;
    }
    return length;
  }, [value]);

  const currentLength = getTextLength();
  const remaining = maxCharLength - currentLength;

  const getCharCountStyles = () => {
    const warningThreshold = maxCharLength * 0.8;
    const errorThreshold = maxCharLength * 0.95;

    if (currentLength >= errorThreshold) {
      return { color: '#ff4d4f', backgroundColor: '#fff2f0', borderColor: '#ffccc7' };
    }
    if (currentLength >= warningThreshold) {
      return { color: '#faad14', backgroundColor: '#fffbe6', borderColor: '#ffe58f' };
    }
    return { color: '#52c41a', backgroundColor: '#f6ffed', borderColor: '#b7eb8f' };
  };

  const charCountStyles = getCharCountStyles();

  return (
    <SpaceBetween size="m">
      {readOnly && value ? (
        // Use SafeHtml for readonly mode with embedded images
        <TextContent>
          <SafeHtml html={value} mediaResponse={mediaResponse} />
        </TextContent>
      ) : (
        // Use ReactQuill for edit mode
        <>
          <ReactQuill
            ref={quillRef}
            readOnly={readOnly}
            theme={theme}
            onChange={(v) => {
              if (onChange) {
                // Check if content is effectively empty (ReactQuill returns <p><br></p> for empty)
                const isEmpty = v === '<p><br></p>' || !v.trim();
                onChange(isEmpty ? '' : v);

                const attachedImages = extractSrcValues(v || '');
                setImageList((prev) =>
                  prev.filter((img) => attachedImages.includes(img.image)),
                );
              }
            }}
            value={value}
            defaultValue={''}
            modules={modules}
            formats={formats}
            bounds={'#root'}
            style={{ minHeight: minHeight || 100 }}
            placeholder={placeholder}
          />

          <Box
            fontSize="body-m"
            color={
              currentLength > maxCharLength ? 'text-status-error' : 'text-body-secondary'
            }
          >
            {currentLength > maxCharLength
              ? `Character limit exceeded: ${currentLength}/${maxCharLength}`
              : `Maximum Character Limit: ${maxCharLength}`}
          </Box>

          {/* File Upload Section - Only visible when showFileUpload is true and not readonly */}
          {showFileUpload && (
            <>
              <FileUpload
                onChange={({ detail }) => handleFileUploadChange(detail.value)}
                value={uploadedFiles.filter((file) => file.size > 0)}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                  dropzoneText: (e) =>
                    e ? 'Drop files to upload' : 'Drop file to upload',
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm"
                multiple
                showFileSize
                showFileThumbnail
              />
              {/* Display existing file metadata with remove option in edit mode */}
              {fileMetadata.length > 0 && (
                <FileDisplay
                  files={fileMetadata}
                  readonly={false}
                  onRemove={handleFileRemove}
                />
              )}
            </>
          )}
        </>
      )}

      {/* Read-only file display - Only visible when readonly and metadata exists */}
      {readOnly && fileMetadata.length > 0 && (
        <FileDisplay files={fileMetadata} readonly={true} />
      )}
    </SpaceBetween>
  );
};

// Quill modules configuration
const modules = {
  toolbar: {
    container: [
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
  },
  clipboard: {
    matchVisual: false,
  },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize'],
  },
};

// Quill formats
const formats = [
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'width',
  'style',
];

export default TextEditor;
